import { MAX_SELECTABLE_GENRE_TAGS } from '@arpeggi/shared'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { useEffect, useState } from 'react'
import ReactGA from 'react-ga'
import { useArpDraftsAPI } from '../../../../api/v2/ArpDraftsAPI'
import useMintGasless from '../../../../mint/MintGasless'
import useMintToDatabase from '../../../../mint/MintToDatabase'
import { useCreateDraft, useUpdateDraft } from '../../../../queries/drafts'
import { useTreeState } from '../../../../stores/Root'
import { IStudioStore } from '../../../../stores/StudioStore'
import { IHoneyPotFields } from '../../../core/HoneyPotFields'
import { MINTABLE_COLORS } from '../../../platform/color'
import { exportStudioForMint } from '../../StudioUtils'

const segmentSongMinted = (song, genres) => {
  window.analytics.track('Song Minted', {
    source: window.location.pathname,
    name: song.name,
    lengthInSeconds: song.lengthInSeconds,
    numberOfStems: song.stems.length,
    numberOfSamples: song.currentSamples.length,
    genres: genres
  })
}

const download = (fileUrl, fileName) => {
  let a = document.createElement('a')
  a.href = fileUrl
  a.setAttribute('download', fileName)
  a.click()
}

const useMintModalSteps = ({
  stepIndex,
  stepForward,
  studio,
  customArtistAddress
}: {
  stepIndex: number
  stepForward: () => void
  studio: IStudioStore
  customArtistAddress?: string
}) => {
  const flags = useFlags()
  const { player } = useTreeState()
  const createDraft = useCreateDraft()
  const updateDraft = useUpdateDraft()
  const { getDrafts } = useArpDraftsAPI()
  const { mintSongGasless } = useMintGasless()
  const { mintSongToDatabase } = useMintToDatabase()
  const [genres, setGenres] = useState<Set<string>>(new Set())
  const [detailPagePath, setDetailPagePath] = useState(undefined)
  const [unableToMintReason, setUnableToMintReason] = useState(undefined)
  // Should not be filled out
  const [honeyPotFields, setHoneyPotFields] = useState<IHoneyPotFields>({
    firstName: '',
    lastName: ''
  })

  useEffect(() => {
    if (stepIndex === 1) {
      handleMint(customArtistAddress)
    }
  }, [stepIndex, customArtistAddress])

  useEffect(() => {
    studio.removeStemSolo()
    studio.unselectStem()
    // Important to minimize the song before checking if it has valid stems.
    studio.song.minimizeForStorage()

    // If we are on the first step and there is no stems, we cannot mint
    if (studio.song.stems.length === 0 && stepIndex === 0) {
      setUnableToMintReason('No stems or samples containing notes were found.')
    }
    // Currently, a bug in our lambdas prevents minting songs with only remixed stems.
    if (studio.song.stems.filter((stem) => !stem.isRemixed).length === 0) {
      setUnableToMintReason('Minting songs with only remixed stems is not allowed yet.')
    }
  }, [studio.song.stems.length, stepIndex])

  const handleGenreSelect = (genre: string) => {
    const set = new Set(genres)
    if (set.size < MAX_SELECTABLE_GENRE_TAGS) {
      set.add(genre)
    }

    setGenres(set)
  }

  const handleGenreRemove = (genre: string) => {
    const set = new Set(genres)
    set.delete(genre)

    setGenres(set)
  }

  let jobId

  const handleMint = async (customArtistAddress?: string) => {
    player.stop()

    const songCompositionDto = exportStudioForMint(studio)

    jobId = await mintSongGasless({
      studio,
      song: studio.song,
      songCompositionDto,
      genres: Array.from(genres),
      customArtistAddress,
      honeyPotFields
    })
    setDetailPagePath(`/song-preview/${jobId}`)

    ReactGA.event({
      category: 'Studio',
      action: 'Minted a song'
    })
    segmentSongMinted(studio.song, Array.from(genres))

    // Save the song as a "Minted" draft, then clear it
    const draftName = studio.song.name + ' (Minted)'
    const song = studio.song.serializeSongForStorage()

    const drafts = (await getDrafts({ name: draftName, perPage: 1 })).data
    if (drafts.length) {
      await updateDraft.mutateAsync({ id: drafts[0].id, dto: { name: draftName, arp: song } })
    } else {
      await createDraft.mutateAsync({ name: draftName, arp: song })
    }

    studio.resetToNewSongState()
    stepForward()
  }

  useEffect(() => {
    if (!studio.song.tokenColor) studio.song.setTokenColor(MINTABLE_COLORS[0])
  }, [studio.song])

  return {
    genres,
    handleGenreSelect,
    handleGenreRemove,
    handleMint,
    detailPagePath,
    unableToMintReason,
    honeyPotFields,
    setHoneyPotFields
  }
}

export default useMintModalSteps
